<template>
  <div class="flex flex-col">
    <template v-if="loading">
      <slot name="loading" />
    </template>
    <template v-else>
      <button
        class="text-grays-darkest mb-5 inline-flex items-center gap-[11px] self-end rounded-lg border border-[#808080] bg-white px-2.5 py-[9px] font-semibold leading-5"
        @click="hideFilters"
      >
        <span>Hide filters</span>
        <img
          alt="Show or Hide Filters"
          src="/img/icons/filter.svg"
          class="w-5"
        />
      </button>
      <template v-for="(filter, i) in groupedFilters" :key="i">
        <component
          v-if="!Array.isArray(filter)"
          :is="componentMapper[filter.facet.label]"
          :title="filter.title"
          :facet="filter.facet"
          :label-order="filter.labelOrder"
          :selected="
            filtersSelected[
              filter.facet.label as keyof typeof filtersSelected
            ] || []
          "
          :initial-open="filter.filterOrder === 1 ? true : false"
          @update:selected="
            (selected: string[]) =>
              updateSelected(filter.facet.label as Filters, selected)
          "
        />
        <CheckboxCategoriesItem
          v-else
          title="Frame Style"
          :categories="filter"
          :selected="filtersSelected"
          :initial-open="filter?.[0]?.filterOrder === 1 ? true : false"
          @update:selected="
            ({ key, value }) => updateSelected(key as Filters, value)
          "
        />
      </template>
    </template>
  </div>
</template>
<script setup lang="ts">
import { ref, toRefs, computed } from 'vue'
import { fromPairs, sortBy, toPairs } from 'lodash-es'
import CheckboxItem from './CheckboxItem/CheckboxItem.vue'
import HasGenderItem from './HasGenderItem/HasGenderItem.vue'
import HasPrescriptionItem from './HasPrescriptionItem/HasPrescriptionItem.vue'
import CheckboxRangeItem from './CheckboxRangeItem/CheckboxRangeItem.vue'
import ChipSelector from './ChipSelector/ChipSelector.vue'
import ColorDotItem from './ColorDotItem/ColorDotItem.vue'
import CheckboxCategoriesItem from './CheckboxCategoriesItem/CheckboxCategoriesItem.vue'
import { filterOrderClass as filterOrderClassImport } from './utils'
import type {
  Filter,
  Filters,
  SelectedFilters,
  ZnFilterProps,
} from '~/components/Zenni/Plp/filter-types'
import { BUDGET_FILTERS } from '~/data/budget-filters'
import { useGuestStore } from '~/store/guest'
import type { ZnFilterEmits } from './types'

const showFilter = ref(true)

const { giftAmount } = useGuestStore()

const selectedFilter = BUDGET_FILTERS.sort((a, b) => b.price - a.price).find(
  (filter) => filter.price <= giftAmount,
)

const componentMapper: any = {
  gender: HasGenderItem,
  Frame___Prescription_types_search: HasPrescriptionItem,
  price: CheckboxItem,
  size: ChipSelector,
  frameStyle: CheckboxItem,
  color: ColorDotItem,
  brand: CheckboxItem,
  material: CheckboxItem,
  Price_search: CheckboxRangeItem,
  Frame___Gender_search: HasGenderItem,
  Frame___Size_search: ChipSelector,
  Frame___Shape_search: CheckboxItem,
  Main_Color_search: ColorDotItem,
  Brand_search: CheckboxItem,
  Frame___Material_search: CheckboxItem,
  Frame___Rim_type_search: CheckboxItem,
  Frame___Features_search: CheckboxItem,
  Is_Under_25_Limit: CheckboxItem,
  Is_Under_50_Limit: CheckboxItem,
  Is_Under_75_Limit: CheckboxItem,
  Is_Under_100_Limit: CheckboxItem,
  Is_Under_125_Limit: CheckboxItem,
  Is_Under_150_Limit: CheckboxItem,
  Is_Under_175_Limit: CheckboxItem,
  Is_Under_200_Limit: CheckboxItem,
}

const emit = defineEmits<ZnFilterEmits>()
const props = withDefaults(defineProps<ZnFilterProps>(), {
  filters: () => ({}) as Filter,
  selectedFilters: () => ({}) as SelectedFilters,
  loading: false,
  grouped: () => [],
  freeGlasses: () => [],
})

const { selectedFilters } = toRefs(props)
const filtersSelected = ref<SelectedFilters>(selectedFilters.value)

const filterOrderClass = filterOrderClassImport as {
  [key: number]: string
}

const groupedFilters = computed(() => {
  const sortedFilters = fromPairs(
    sortBy(
      toPairs(props.filters).filter((item) => componentMapper[item[0]]),
      (item) => item[1].filterOrder ?? 999,
    ),
  )

  const allFilters = new Array(sortedFilters.length)
  let i = 0

  for (const key of Object.keys(sortedFilters)) {
    if (props.grouped.includes(key)) {
      if (allFilters[props.filters[key as keyof Filter].filterOrder]) {
        allFilters[props.filters[key as keyof Filter].filterOrder].push(
          props.filters[key as keyof Filter] as Filter,
        )
      } else {
        allFilters[props.filters[key as keyof Filter].filterOrder] = [
          props.filters[key as keyof Filter] as Filter,
        ]
      }
    } else if (props.freeGlasses.includes(key)) {
      if (filtersSelected.value?.[key as keyof SelectedFilters]?.length) {
        const filter = props.filters[key as keyof Filter] as Filter
        allFilters[0] = {
          ...filter,
          facet: {
            ...filter.facet,
            values: filter.facet.values.filter((val) => val.value === 'true'),
          },
          title: 'Free Glasses',
        }
      }
    } else {
      allFilters[props.filters[key as keyof Filter].filterOrder ?? i] = props
        .filters[key as keyof Filter] as Filter
    }
    i++
  }
  return allFilters.filter(Boolean)
})

const updateSelected = (filterCat: Filters, selected: string[]) => {
  if (!selected.length) {
    delete filtersSelected.value[filterCat]
  } else {
    filtersSelected.value[filterCat] = selected
  }
  emit('update:selectedFilters', filtersSelected.value)
  emit('apply', filtersSelected.value)
}

const hideFilters = () => {
  showFilter.value = !showFilter.value
  if (!showFilter.value) {
    emit('hide-filters')
  }
}
</script>
